import {
  ApiProductColor,
  ApiProduct,
  ApiProductLocation,
  ApiProductMedia,
  ApiProductXmedia,
} from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import {
  ApiStoreXmedia, ApiStoreXmediaFormat,
  ApiStoreXmediaSizeItem, ApiStoreXmediaSet, ApiStoreXmediaSize
} from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';

import {
  Media, MediaClazz, MediaLocationTypeValues, MediaSize, MediaContentType,
  MediaItemsLocationPositionMap, MediaLocationWithPosition
} from './medias.models';


export class MediasModelsTransformer {

  public static mediasFromProductApi(
    color: ApiProductColor,
    product: ApiProduct,
    context: EcommerceContext,
  ): Media[] {
    const fallbackMedias = this.calculateFallbackMedias(color, product, context);
    const xmediaMedias = this.calculateMediasFromXmedia(color, product, context);
    const medias = xmediaMedias.concat(fallbackMedias);
    return medias;
  }

  // ---- Private functions ----

  // TODO: there are more than one fallback media (by location and clazz ...)
  private static calculateFallbackMedias(
    color: ApiProductColor,
    product: ApiProduct,
    context: EcommerceContext,
  ): Media[] {
    const fallbackMedias: Media[] = [];
    const clazz: MediaClazz = { id: 1, type: 'main' };
    const path = this.calculateMediaFallbackPath(color, context);

    if (path) {
      const contentType: MediaContentType = { type: 'image', subtype: '.jpg' };
      const position = 0;
      const productId = product.id.toString();
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const metadata = { _debug: { productId }, colorName: color.name };

      for (const location of MediaLocationTypeValues) {
        const fallbackMedia: Media = {
          location, position, clazz, isFallback: true, path, metadata, contentType
        };
        fallbackMedias.push(fallbackMedia);
      }
    }
    return fallbackMedias;
  }

  private static calculateMediaFallbackPath(color: ApiProductColor, context: EcommerceContext): string | undefined {
    let path;

    if (isDefined(context) && isDefined(context.store.details) && isDefined(context.store.details.imageBaseUrl)
        && isDefined(color.image) && isDefined(color.image.url)) {
      const mediaPathSuffix = context.config.brandSettings.mediaPathSuffix;
      const imageBaseUrl = context.store.details.imageBaseUrl;
      const timestamp = color.image.timestamp;
      path = `${imageBaseUrl}${color.image.url}_${mediaPathSuffix}.jpg?t=${timestamp}`;
    }
    return path;
  }

  /** You can find doc about this algorithm in doc/itx-ecommerce-api/XMEDIA.pdf */
  private static calculateMediasFromXmedia(
    color: ApiProductColor,
    product: ApiProduct,
    context: EcommerceContext,
  ): Media[] {
    const store = context.store;
    const storeXmedia = store.details._xmedia;
    const colorName = color.name;

    // Variables que dependerán de si es un ApiProduct o un ApiOrderProduct
    let productXmedia: ApiProductXmedia[] = [];
    let xmediaDefaultSetId: number | undefined | null;
    let defaultImageType: string | undefined | null;

    // Get xmedia depending on product type
    if (product.detail) {
      // Es un ApiProduct
      productXmedia = product.detail.xmedia ?? [];
      xmediaDefaultSetId = product.detail.xmediaDefaultSet;
      defaultImageType = product.detail.defaultImageType;
    } else {
      // Es un ApiOrderProduct
      productXmedia = product.xmedia ?? [];
      xmediaDefaultSetId = product.xmediaDefaultSet;
    }

    let xmediaMedias: Media[] = [];

    if (!isDefined(store.details) || !isDefined(store.details._xmedia) || !isDefined(storeXmedia) || !isDefined(productXmedia)) {
      return [];
    }

    // find xmedia for current color
    const xmediaColor = productXmedia.find((xmedia) => xmedia.colorCode === color.id);

    const xmediaDefaultSet = this.calculateXmediaSet(storeXmedia, xmediaDefaultSetId);

    if (xmediaDefaultSet?.baseUri && isDefined(xmediaColor) && isDefined(xmediaColor.path)
    && isDefined(xmediaColor.xmediaLocations) && isDefined(xmediaColor.xmediaItems)) {
      const nonDefaultXMediaSets = storeXmedia.xmediaSets.filter(set => set.set !== xmediaDefaultSet.set);
      // We put the default set first
      const allSets = [xmediaDefaultSet, ...nonDefaultXMediaSets];
      for (const xmediaSet of allSets) {
        const setId = xmediaSet.set;
        const mediaBasePath = xmediaColor.path;

        // find xmedia locations, xmedia items and xmedia sizes for current set
        const xmediaSetLocations = xmediaColor.xmediaLocations.find((xmediaLocation) => xmediaLocation.set === setId);
        const xmediaSetSizes = storeXmedia.xmediaSizes.find((xmediaSize) => xmediaSize.set === setId);
        const xmediaSetItems = xmediaColor.xmediaItems.find((xmediaItem) => xmediaItem.set === setId);

        if (isDefined(xmediaSetLocations) && isDefined(xmediaSetLocations.locations) && isDefined(xmediaSetSizes)
            && isDefined(xmediaSetItems) && isDefined(xmediaSetItems.medias)) {
          // create a map (map[mediaId] = [{location, position}]) to easy find in which locations (and position) was found the mediaItem
          const xmediaItemsLocationsMap = this.calculateMediaItemsLocationsMap(xmediaSetLocations.locations, defaultImageType);

          // iterate over all product mediaItems
          for (const xmediaItem of xmediaSetItems.medias) {
            const mediasForMediaItem = this.calculateXmediasFromXmediaItem(
              xmediaItem,
              xmediaItemsLocationsMap,
              xmediaSetSizes,
              storeXmedia,
              mediaBasePath,
              xmediaSet,
              context,
              product,
              colorName
            );

            xmediaMedias = xmediaMedias.concat(mediasForMediaItem);
          }
        }
      }
    }
    return xmediaMedias;
  }

  private static calculateXmediaSet(
    storeXmedia: ApiStoreXmedia,
    xmediaDefaultSet: undefined | number | null,
  ): ApiStoreXmediaSet | undefined {
    const setId = xmediaDefaultSet || storeXmedia.defaultStoreSet;
    let xmediaSet: ApiStoreXmediaSet | undefined;

    if (storeXmedia?.xmediaSets?.find) {
      xmediaSet = storeXmedia.xmediaSets.find((mediaSet) => mediaSet.set === setId);
    }

    return xmediaSet;
  }

  /* map with mediaitemId as key and an array of location, position as value */
  /* ex. map['0112300812_5_1_'] = [{location: 'category_page', position: 0}, ....] */
  private static calculateMediaItemsLocationsMap(
    apiLocations: ApiProductLocation[],
    defaultImageType?: string | null,
  ) {
    const map: MediaItemsLocationPositionMap = {};
    for (const apiLocation of apiLocations) {
      let mediaItemIds = apiLocation.mediaLocations;
      const locationIndex = apiLocation.location;

      if (isDefined(locationIndex) && locationIndex < MediaLocationTypeValues.length) {
        const location = MediaLocationTypeValues[locationIndex];

        // override media locations if defaultImageType (in grid and detail)
        if (isDefined(defaultImageType) && (location === 'category_page' || location === 'product_page')) {
          mediaItemIds = [defaultImageType];
        }

        if (isDefined(mediaItemIds)) {
          for (let i = 0; i < mediaItemIds.length; i++) {
            const mediaItemId = mediaItemIds[i];
            const position = i;

            if (!isDefined(map[mediaItemId])) {
              map[mediaItemId] = [];
            }
            map[mediaItemId].push({ location, position });
          }
        }
      }
    }
    return map;
  }

  private static calculateXmediasFromXmediaItem(
    xmediaItem: ApiProductMedia,
    xmediaItemsLocationsMap: MediaItemsLocationPositionMap,
    xmediaSetSizes: ApiStoreXmediaSize,
    storeXmedia: ApiStoreXmedia,
    mediaBasePath: string,
    xmediaSet: ApiStoreXmediaSet,
    context: EcommerceContext,
    product: ApiProduct,
    colorName: string,
  ): Media[] {
    const medias: Media[] = [];
    const xmediaItemId = xmediaItem.idMedia;

    if (!isDefined(xmediaItemId)) {
      return medias;
    }

    const locationsWithPositions = xmediaItemsLocationsMap[xmediaItemId];

    if (!isDefined(locationsWithPositions)) {
      return medias;
    }

    const xmediaSizeItem = xmediaSetSizes.sizeItems.find((xmediaSize) => xmediaSize.clazz === xmediaItem.clazz
                                                                         && xmediaSize.format === xmediaItem.format);

    if (!isDefined(xmediaSizeItem)) {
      return medias;
    }

    const xmediaFormat = storeXmedia.xmediaFormats.find((mediaFormat) => mediaFormat.format === xmediaSizeItem.format);

    if (!isDefined(xmediaFormat) || (!isDefined(mediaBasePath))) {
      return medias;
    }

    for (const locationWithPosition of locationsWithPositions) {
      if (!isDefined(xmediaFormat)) {
        return medias;
      }
      const media = this.createMediaFromXmedia(
        context,
        xmediaSet,
        mediaBasePath,
        locationWithPosition,
        xmediaItem,
        xmediaFormat,
        xmediaSizeItem,
        product.id.toString(),
        colorName
      );

      if (media) {
        medias.push(media);
      }
    }

    return medias;
  }

  private static createMediaFromXmedia(
    context: EcommerceContext,
    xmediaSet: ApiStoreXmediaSet,
    mediaBasePath: string,
    locationWithPosition: MediaLocationWithPosition,
    xmediaItem: ApiProductMedia,
    xmediaFormat: ApiStoreXmediaFormat,
    xmediaSizeItem: ApiStoreXmediaSizeItem,
    productId: string,
    colorName: string
  ): Media | undefined {
    let media: Media | undefined;
    const setBaseUri = isDefined(xmediaSet) ? xmediaSet.baseUri : undefined;
    const set = xmediaSet?.set;
    const mediaFolder = xmediaSet.folder;
    const mediaItemId = xmediaItem.idMedia;
    const mediaExtension = xmediaFormat.extension;
    const productMediaTimestamp = xmediaItem.timestamp;
    const mediaSizes: MediaSize[] = xmediaSizeItem.sizes;
    const mediaItemExtraInfo = xmediaItem.extraInfo;

    if (isDefined(mediaItemId) && isDefined(mediaExtension)
      && isDefined(productMediaTimestamp) && isDefined(xmediaItem.clazz)) {
      let clazz = this.parseMediaClazz(xmediaItem.clazz);

      if (context.config.brandSettings.parseMediaClazzOverride) {
        clazz = context.config.brandSettings.parseMediaClazzOverride(xmediaItem.clazz);
      }

      const mediaContentType = this.parseContentType(mediaExtension);

      const location = locationWithPosition.location;
      const position = locationWithPosition.position;

      if (mediaContentType.type !== 'unknown') {
        const path = `${setBaseUri}${mediaBasePath}${mediaFolder}/${mediaItemId}{{sizeCode}}${mediaExtension}?t=${productMediaTimestamp}`;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const metadata = { _debug: { productId }, colorName };
        media = {
          path,
          isFallback: false,
          clazz,
          set,
          contentType: mediaContentType,
          location,
          position,
          sizes: mediaSizes,
          metadata,
          extraInfo: mediaItemExtraInfo
        };
      }
    }
    return media;
  }

  // Ref: https://axinic.central.inditex.grp/confluence/display/ECOMNEG/Massimo+Dutti+-+Proyecto+XMEDIA
  private static parseMediaClazz(apiClazz: number): MediaClazz {
    let mediaClazz: MediaClazz;

    switch (apiClazz) {
      case 1:
        mediaClazz = { id: apiClazz, type: 'main' };
        break;
      case 2:
        mediaClazz = { id: apiClazz, type: 'secondary' };
        break;
      case 3:
        mediaClazz = { id: apiClazz, type: 'colorcut' };
        break;
      case 4: case 5:
        mediaClazz = { id: apiClazz, type: 'silhouette' };
        break;
      case 6:
        mediaClazz = { id: apiClazz, type: 'alternative' };
        break;
      case 7:
        mediaClazz = { id: apiClazz, type: 'double' };
        break;
      case 8:
        mediaClazz = { id: apiClazz, type: 'video' };
        break;
      case 10:
        mediaClazz = { id: apiClazz, type: 'quadruple' };
        break;
      default:
        mediaClazz = { id: apiClazz, type: 'unknown' };
        break;
    }

    return mediaClazz;
  }

  // Ref: https://axinic.central.inditex.grp/confluence/display/ECOMNEG/Bershka+-+Proyecto+XMEDIA
  // Ref: https://axinic.central.inditex.grp/confluence/display/ECOMNEG/Zara+Home+-+Proyecto+XMEDIA
  // Ref: https://axinic.central.inditex.grp/confluence/display/ECOMNEG/Oysho+-+Proyecto+XMEDIA
  private static parseContentType(mediaExtension: string): MediaContentType {
    let mediaContentType: MediaContentType;
    switch (mediaExtension) {
      case '.jpg': case '.jpeg': case '.gif': case '.png':
        mediaContentType = { type: 'image', subtype: mediaExtension.substr(1) };
        break;
      case '.mp4': case '.webm':
        mediaContentType = { type: 'video', subtype: mediaExtension.substr(1) };
        break;
      default:
        mediaContentType = { type: 'unknown', subtype: mediaExtension.substr(1) };
        break;
    }

    return mediaContentType;
  }

}
